"use client"

import { Box, Button, IconButton, Typography, Grid } from "@mui/material"
import { useState } from "react"
import { MdChevronLeft, MdChevronRight } from "react-icons/md"
import theme from "../../../theme"
import { isBefore, startOfDay, isSameDay } from "date-fns"
import React from "react"

export function SimpleCalendar({
  selectedDate,
  onChange,
  disablePastDates = false,
}: {
  selectedDate: Date | null
  onChange: (date: Date) => void
  disablePastDates?: boolean
}) {
  const [currentMonth, setCurrentMonth] = useState(selectedDate ? new Date(selectedDate) : new Date())

  // Set day to 1 to get the first day of the month
  const firstDayOfCurrentMonth = new Date(currentMonth)
  firstDayOfCurrentMonth.setDate(1)

  const month = firstDayOfCurrentMonth.getMonth()
  const year = firstDayOfCurrentMonth.getFullYear()

  const daysInMonth = new Date(year, month + 1, 0).getDate()
  const firstDayOfMonth = new Date(year, month, 1).getDay()

  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ]

  const dayNames = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"]

  const handlePrevMonth = () => {
    const newMonth = new Date(firstDayOfCurrentMonth)
    newMonth.setMonth(newMonth.getMonth() - 1)
    setCurrentMonth(newMonth)
  }

  const handleNextMonth = () => {
    const newMonth = new Date(firstDayOfCurrentMonth)
    newMonth.setMonth(newMonth.getMonth() + 1)
    setCurrentMonth(newMonth)
  }

  const handleDateClick = (day: number) => {
    const newDate = new Date(year, month, day)
    onChange(newDate)
  }

  const isSelectedDate = (day: number) => {
    if (!selectedDate) return false
    const dateToCheck = new Date(year, month, day)
    return isSameDay(selectedDate, dateToCheck)
  }

  const isDateDisabled = (day: number) => {
    if (!disablePastDates) return false

    const dateToCheck = new Date(year, month, day)
    const today = startOfDay(new Date())

    return isBefore(dateToCheck, today)
  }

  // Generate calendar grid
  const calendarDays = []

  // Add empty cells for days before the first day of the month
  for (let i = 0; i < firstDayOfMonth; i++) {
    calendarDays.push(<Box key={`empty-${i}`} />)
  }

  // Add days of the month
  for (let day = 1; day <= daysInMonth; day++) {
    const disabled = isDateDisabled(day)

    calendarDays.push(
      <Box
        key={day}
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "36px",
        }}
      >
        <Button
          onClick={() => !disabled && handleDateClick(day)}
          variant={isSelectedDate(day) ? "contained" : "text"}
          disabled={disabled}
          sx={{
            minWidth: "36px",
            width: "36px",
            height: "36px",
            borderRadius: "6px",
            padding: 0,
            backgroundColor: isSelectedDate(day) ? theme.palette.colors.green["700"] : "transparent",
            color: isSelectedDate(day)
              ? "white"
              : disabled
                ? theme.palette.colors.black["300"]
                : theme.palette.colors.black["700"],
            "&:hover": {
              backgroundColor: isSelectedDate(day)
                ? theme.palette.colors.green["700"]
                : disabled
                  ? "transparent"
                  : "rgba(0, 0, 0, 0.04)",
            },
            cursor: disabled ? "not-allowed" : "pointer",
          }}
        >
          {day}
        </Button>
      </Box>,
    )
  }

  const isPrevMonthDisabled = disablePastDates && month === new Date().getMonth() && year === new Date().getFullYear()

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "336px",
        margin: "0 auto",
        border: `1px solid ${theme.palette.colors.black["200"]}`,
        borderRadius: "8px",
        padding: "16px",
        backgroundColor: theme.palette.colors.white["400"],
      }}
    >
      {/* Header with month/year and navigation */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <IconButton
          onClick={handlePrevMonth}
          size="small"
          disabled={isPrevMonthDisabled}
          sx={{
            color: isPrevMonthDisabled ? theme.palette.colors.black["300"] : theme.palette.colors.black["600"],
          }}
        >
          <MdChevronLeft />
        </IconButton>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            color: theme.palette.colors.black["800"],
          }}
        >
          {monthNames[month]} {year}
        </Typography>
        <IconButton
          onClick={handleNextMonth}
          size="small"
          sx={{
            color: theme.palette.colors.black["600"],
          }}
        >
          <MdChevronRight />
        </IconButton>
      </Box>

      {/* Day names */}
      <Grid container spacing={0}>
        {dayNames.map((day) => (
          <Grid item xs={12 / 7} key={day} sx={{ textAlign: "center" }}>
            <Typography
              variant="caption"
              sx={{
                fontWeight: "bold",
                color: theme.palette.colors.black["600"],
              }}
            >
              {day}
            </Typography>
          </Grid>
        ))}
      </Grid>

      {/* Calendar grid */}
      <Grid container spacing={0} sx={{ mt: 1 }}>
        {calendarDays.map((day, index) => (
          <Grid item xs={12 / 7} key={index}>
            {day}
          </Grid>
        ))}
      </Grid>
    </Box>
  )
}

